import React, { createContext, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import type { Chat, QuantAnalysisSourceInfo, QuantAnalysisWidgetSourceMetadata, WidgetInstance } from '@trinity/chat';
import { ChatCitationType, SegmentationDashboardWidget, WidgetSourceType } from '@trinity/chat';
import type { ResearchQuoteWidgetData } from '@trinity/chat/types/dashboard-data';
import { SystemMessageContainer } from '../../Chat/Message.System.Container.js';
import { MessageCitations } from '../../Chat.Message.Citations/index.js';
import { ResponsePane } from '../../ProjectTags/PatientJourneyDashboard/ResponsePane.js';
import { WidgetSourceInfo } from '../../WidgetGrid/WidgetSourceInfo.js';

export type TranscriptSource = ResearchQuoteWidgetData['source'];

type TranscriptSpeaker = { name?: string; start?: number; end?: number };

/** Resolves a transcript source to a link; null when the host has no transcript page for it. */
export type QuoteSourceLinkResolver = (source: TranscriptSource) => string | null;

/** Resolves a quant analysis source to a link; null when the host has no page for it. */
export type QuantSourceLinkResolver = (source: QuantAnalysisSourceInfo) => string | null;

const QuoteSourceLinkContext = createContext<QuoteSourceLinkResolver | null>(null);
const QuantSourceLinkContext = createContext<QuantSourceLinkResolver | null>(null);

/** Lets a host (PJ, segments, …) make transcript sources clickable. Without it, sources show unlinked. */
export function QuoteSourceLinkProvider({
  resolve,
  children,
}: {
  resolve: QuoteSourceLinkResolver;
  children: React.ReactNode;
}): React.ReactElement {
  return <QuoteSourceLinkContext.Provider value={resolve}>{children}</QuoteSourceLinkContext.Provider>;
}

/** Lets a host make quant analysis sources clickable. Each app routes projects differently. */
export function QuantSourceLinkProvider({
  resolve,
  children,
}: {
  resolve: QuantSourceLinkResolver;
  children: React.ReactNode;
}): React.ReactElement {
  return <QuantSourceLinkContext.Provider value={resolve}>{children}</QuantSourceLinkContext.Provider>;
}

const LibraryWidgetSourcesContext = createContext(false);

/** Turns on the Sources pane for saved library widgets. Off by default (see `PageBuilderProfile.sources`). */
export function LibraryWidgetSourcesProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return <LibraryWidgetSourcesContext.Provider value={enabled}>{children}</LibraryWidgetSourcesContext.Provider>;
}

export function useLibraryWidgetSourcesEnabled(): boolean {
  return useContext(LibraryWidgetSourcesContext);
}

/** Where a widget's data came from: a transcript (quotes, media) or a quant analysis (charts, KPIs). */
export type WidgetSourceRef =
  | { kind: 'transcript'; source: TranscriptSource; speaker?: TranscriptSpeaker; text: string }
  | { kind: 'quant-analysis'; metadata: QuantAnalysisWidgetSourceMetadata };

/** The single-transcript citation the Sources pane lists. `text` is the quoted passage shown under the speaker. */
export function buildTranscriptCitations({
  source,
  link,
  speaker,
  text,
}: {
  source: TranscriptSource;
  link: string | null;
  speaker?: TranscriptSpeaker;
  text: string;
}): Chat.Citation[] {
  return [
    {
      ordinal: 1,
      link,
      type: ChatCitationType.Transcript,
      score: null,
      title: `Transcript (${source.transcriptId})`,
      snippets: [
        {
          ordinal: 1,
          score: null,
          title: null,
          text: `#### ${speaker?.name ?? ''}\n${text}`,
          metadata: {
            start: speaker?.start ?? 0,
            end: speaker?.end ?? 0,
            project_id: source.projectId,
            call_id: source.callId,
          },
          isMarkdown: true,
        },
      ],
    },
  ] as unknown as Chat.Citation[];
}

type TextNodeLike = { text?: string; content?: TextNodeLike[] };

/** The plain text of a stored Remirror document: its text leaves, concatenated. */
export function plainTextOf(node: TextNodeLike | undefined): string {
  if (!node) return '';
  if (typeof node.text === 'string') return node.text;
  return (node.content ?? []).map(plainTextOf).join('');
}

type LibrarySourceProps = {
  source?: TranscriptSource;
  speaker?: TranscriptSpeaker;
  quote?: TextNodeLike;
};

/** The passage a saved widget quotes from its transcript; null for widget types that quote none. */
function transcriptPassage(widget: WidgetInstance, props: LibrarySourceProps): string | null {
  switch (widget.type) {
    case SegmentationDashboardWidget.Media:
      return widget.title ?? '';
    case SegmentationDashboardWidget.Quote:
      return plainTextOf(props.quote);
    default:
      return null;
  }
}

/**
 * What a saved library widget cites. Saved quotes and media carry their transcript in `props.source`;
 * widgets saved from a quant analysis carry the analysis in `sourceMetadata`. Other metadata (brand
 * insights, manual) has no link or detail to show, and rich text cites nothing.
 */
export function getLibraryWidgetSources(widget: WidgetInstance): WidgetSourceRef[] {
  const refs: WidgetSourceRef[] = [];
  const props = widget.props as LibrarySourceProps | undefined;

  const text = props?.source?.transcriptId ? transcriptPassage(widget, props) : null;
  if (props?.source && text !== null) {
    refs.push({ kind: 'transcript', source: props.source, speaker: props.speaker, text });
  }

  const metadata = widget.sourceMetadata;
  if (metadata?.type === WidgetSourceType.QuantAnalysis) {
    refs.push({ kind: 'quant-analysis', metadata });
  }
  return refs;
}

/** A collapsible "Sources (n)" pane listing where a widget's data came from. */
export function WidgetSourcesPane({ sources }: { sources: WidgetSourceRef[] }): React.ReactElement | null {
  const resolveTranscriptLink = useContext(QuoteSourceLinkContext);
  const resolveQuantLink = useContext(QuantSourceLinkContext);
  const [open, setOpen] = useState(false);

  const citations = useMemo(
    () =>
      sources.flatMap((ref) =>
        ref.kind === 'transcript'
          ? buildTranscriptCitations({
              source: ref.source,
              link: resolveTranscriptLink?.(ref.source) ?? null,
              speaker: ref.speaker,
              text: ref.text,
            })
          : [],
      ),
    [sources, resolveTranscriptLink],
  );

  if (sources.length === 0) return null;

  return (
    <SystemMessageContainer citations={citations}>
      <ResponsePane title={`Sources (${sources.length})`} open={open} onToggle={() => setOpen((v) => !v)}>
        {citations.length > 0 && <MessageCitations items={citations} />}
        {sources.map((ref) =>
          ref.kind === 'quant-analysis' ? (
            <WidgetSourceInfo
              key={`${ref.metadata.quantAnalysis.quantAnalysisId}:${ref.metadata.quantAnalysis.itemId}`}
              sourceMetadata={ref.metadata}
              sourceUrl={resolveQuantLink?.(ref.metadata.quantAnalysis) ?? null}
            />
          ) : null,
        )}
      </ResponsePane>
    </SystemMessageContainer>
  );
}

/** The Sources pane for a resolved library widget, when the page's profile turns sources on. */
export function LibraryWidgetSources({ widget }: { widget: WidgetInstance }): React.ReactElement | null {
  const enabled = useLibraryWidgetSourcesEnabled();
  const sources = useMemo(() => getLibraryWidgetSources(widget), [widget]);
  if (!enabled) return null;
  return <WidgetSourcesPane sources={sources} />;
}

/**
 * A library widget's layout in its cell: the header and Sources pane stay put and the content between
 * them takes whatever height is left, shrinking (or scrolling, if it can't) as the cell gets shorter.
 * The pane is never pushed out of view, as with a research quote's Sources.
 */
export function WidgetWithSources({
  widget,
  header,
  children,
}: {
  widget: WidgetInstance;
  header?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <FrameRoot>
      {header}
      <FrameContent>{children}</FrameContent>
      <FramePane>
        <LibraryWidgetSources widget={widget} />
      </FramePane>
    </FrameRoot>
  );
}

const FrameRoot = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minWidth: 0,
  minHeight: 0,
  height: '100%',
});

const FrameContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minWidth: 0,
  minHeight: 0,
  overflowX: 'hidden',
  overflowY: 'auto',
  // The widget fills the space it's given, so it resizes with its cell (as charts do).
  '& > *': { flex: '1 1 auto', minHeight: 0 },
});

const FramePane = styled.div({
  flexShrink: 0,
});
